// src/App.js
import React, { useEffect, useState } from 'react';
import AddToHomePrompt from './components/AddToHomePrompt';
import TileGrid from './components/TileGrid';
import ContactView from './components/ContactView';
import AvailabilityRoster from './availability/AvailabilityRoster';
import MyAvailability from './availability/MyAvailability';
import LineMedicPublic from './public/LineMedicPublic';
import DeploymentPublic from './public/DeploymentPublic';
import AssetTagPublic from './public/AssetTagPublic';
import PrivacyPolicy from './public/PrivacyPolicy';
import AppSupport from './public/AppSupport';
import AppReviewDemo from './review/AppReviewDemo';
import AppSettings from './settings/AppSettings';
import RadioReference from './radio/RadioReference';
import SacCountyProtocols from './protocols/SacCountyProtocols';
import FireWeather from './weather/FireWeather';
import DrugCalculator from './calculator/DrugCalculator';
import SavedIaps from './iap/SavedIaps';
import Ics214 from './ics214/Ics214';
import ControlCenter from './control/ControlCenter';
import ControllerVersionBadge from './control/ControllerVersionBadge';
import TrackingProtected from './tracking/TrackingProtected';
import GearHandoffRoute from './tracking/GearHandoffRoute';
import RestockApprovalPage from './tracking/RestockApprovalPage';
import InventoryAdminProtected from './inventory/InventoryAdminProtected';
import AdminHub from './admin/AdminHub';
import AdminAttention from './admin/AdminAttention';
import MemberManagement from './admin/MemberManagement';
import DeploymentDashboard from './admin/DeploymentDashboard';
import InventoryRecallLookup from './admin/InventoryRecallLookup';
import SystemHealth from './admin/SystemHealth';
import AssetTagSetup from './admin/AssetTagSetup';
import ProgramFeedAdmin from './admin/ProgramFeedAdmin';
import PrescribedBurnsAdmin from './admin/PrescribedBurnsAdmin';
import NotificationAudit from './admin/NotificationAudit';
import DoorAccessAdmin from './admin/DoorAccessAdmin';
import ProgramFeed from './program/ProgramFeed';
import PrescribedBurns from './burns/PrescribedBurns';
import RemsDashboard from './rems/RemsDashboard';
import RemsQuickAccess from './rems/RemsQuickAccess';
import ActionCenter from './member/ActionCenter';
import MemberAuthGate from './auth/MemberAuthGate';
import { FIRELINE_MEDIC_BADGE } from './brand/firelineMedicBadge';
import TenantBrandLockup from './brand/TenantBrandLockup';
import { supabase } from './auth/supabase';
import './index.css';
import './theme.css';

const THEME_STORAGE_KEY = 'smfd-line-medic-theme';
const AVAILABILITY_SHEET_ID = '1-a4TOlei-7OkREMwq7W_tFbPAKUVwiwGcgeoevLXJTg';


function isIndividualRemsProfile(profile) {
  return ['rems_temporary', 'rems_permanent'].includes(String(profile?.member_kind || ''));
}

async function signOutMember() {
  await supabase.auth.signOut();
  if (typeof window !== 'undefined') window.__SMFD_MEMBER_ID__ = '';
  window.location.assign('/');
}

function initialTheme() {
  if (typeof window === 'undefined') return 'light';
  const saved = window.localStorage?.getItem(THEME_STORAGE_KEY);
  if (saved === 'dark' || saved === 'light') return saved;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#09100e' : '#edf2f4');
}

const STARTING_THEME = initialTheme();
applyTheme(STARTING_THEME);

function ThemeToggle() {
  const [theme, setTheme] = useState(STARTING_THEME);

  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    applyTheme(next);
    if (typeof window !== 'undefined') window.localStorage?.setItem(THEME_STORAGE_KEY, next);
  };

  return (
    <button
      className="smfd-theme-toggle"
      type="button"
      onClick={toggle}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      <span className="smfd-theme-toggle-icon" aria-hidden="true">{theme === 'dark' ? '☾' : '☀'}</span>
      <span className="smfd-theme-toggle-label">{theme === 'dark' ? 'Dark' : 'Light'}</span>
    </button>
  );
}

function DashboardHeader({ profile, onSignOut }) {
  return (
    <header className="smfd-brand-header">
      <TenantBrandLockup profile={profile} className="smfd-brand-lockup" />

      <div className="smfd-account-actions">
        <div className="smfd-account-pill">
          <small>Signed in as</small>
          <strong>{profile?.full_name || 'Member'}</strong>
        </div>
        <button className="smfd-signout" type="button" onClick={() => window.location.assign('/settings')}>Settings</button>
        <ThemeToggle />
        <button className="smfd-signout" type="button" onClick={onSignOut}>Sign out</button>
      </div>
    </header>
  );
}

function DashboardSubpage({ profile, onSignOut, onBack, kicker, title, subtitle, children }) {
  return (
    <>
      <AddToHomePrompt />
      <div className="app-container">
        <DashboardHeader profile={profile} onSignOut={onSignOut} />
        <section className="dashboard-subpage">
          <div className="dashboard-subpage-head">
            <div>
              <span className="section-kicker">{kicker}</span>
              <h2>{title}</h2>
              {subtitle && <p>{subtitle}</p>}
            </div>
            <button className="back-button" type="button" onClick={onBack}>← Dashboard</button>
          </div>
          {children}
        </section>
      </div>
    </>
  );
}

function MemberDashboard({ profile }) {
  const [activeView, setActiveView] = useState(null);
  const handleBack = () => setActiveView(null);

  if (activeView === 'medics') {
    return (
      <DashboardSubpage
        profile={profile}
        onSignOut={signOutMember}
        onBack={handleBack}
        kicker="AVAILABILITY"
        title="Line Medic Availability"
        subtitle="Live roster status with the latest app-tracked deployment when available."
      >
        <AvailabilityRoster sheetId={AVAILABILITY_SHEET_ID} sheetName="Medics" />
      </DashboardSubpage>
    );
  }

  if (activeView === 'emts') {
    return (
      <DashboardSubpage
        profile={profile}
        onSignOut={signOutMember}
        onBack={handleBack}
        kicker="AVAILABILITY"
        title="Line EMT Availability"
        subtitle="Live roster status with the latest app-tracked deployment when available."
      >
        <AvailabilityRoster sheetId={AVAILABILITY_SHEET_ID} sheetName="EMTs" />
      </DashboardSubpage>
    );
  }

  if (activeView === 'my-availability') {
    return (
      <DashboardSubpage
        profile={profile}
        onSignOut={signOutMember}
        onBack={handleBack}
        kicker="MY AVAILABILITY"
        title="My Availability"
        subtitle="Review your program calendar and add or remove only your own availability."
      >
        <MyAvailability profile={profile} />
      </DashboardSubpage>
    );
  }

  if (activeView === 'radios') {
    return (
      <DashboardSubpage
        profile={profile}
        onSignOut={signOutMember}
        onBack={handleBack}
        kicker="FIELD REFERENCE"
        title="Radio Reference"
        subtitle="Offline-ready CAL FIRE KNG statewide bank and channel reference."
      >
        <RadioReference />
      </DashboardSubpage>
    );
  }

  if (activeView === 'protocols') {
    return (
      <DashboardSubpage
        profile={profile}
        onSignOut={signOutMember}
        onBack={handleBack}
        kicker="FIELD REFERENCE"
        title="Sac County Protocols"
        subtitle="Searchable, offline-ready official Sacramento County EMS policies, procedures and protocols."
      >
        <SacCountyProtocols />
      </DashboardSubpage>
    );
  }

  if (activeView === 'iaps') {
    return (
      <DashboardSubpage
        profile={profile}
        onSignOut={signOutMember}
        onBack={handleBack}
        kicker="FIELD REFERENCE"
        title="Saved IAPs"
        subtitle="Scan the physical IAP QR, choose the actual PDF, then keep a searchable offline copy on this device."
      >
        <SavedIaps />
      </DashboardSubpage>
    );
  }

  if (activeView === 'drug-calculator') {
    return (
      <DashboardSubpage
        profile={profile}
        onSignOut={signOutMember}
        onBack={handleBack}
        kicker="MEDICATION CALCULATOR"
        title="Drug Calculator"
        subtitle="Offline Sacramento County adult and pediatric weight-based dosing, carried concentrations, and draw volumes."
      >
        <DrugCalculator onOpenProtocols={() => setActiveView('protocols')} />
      </DashboardSubpage>
    );
  }

  if (activeView === 'fire-weather') {
    return (
      <DashboardSubpage
        profile={profile}
        onSignOut={signOutMember}
        onBack={handleBack}
        kicker="FIRE WEATHER"
        title="Fire Weather — My Location"
        subtitle="Official NWS digital fire-weather data for the phone’s approximate location, cached for field use."
      >
        <FireWeather />
      </DashboardSubpage>
    );
  }

  const lineMedicOnlyTiles = [
    {
      id: 'program-feed',
      title: 'Program Feed',
      description: 'Program updates, required acknowledgments, and discussion.',
      icon: 'message',
      variant: 'primary',
      onClick: () => window.location.assign('/program-feed'),
    },
    {
      id: 'prescribed-burns',
      title: 'Prescribed Burns',
      description: 'View upcoming burns, sign up, and track your selection status.',
      icon: 'fire',
      variant: 'primary',
      onClick: () => window.location.assign('/prescribed-burns'),
    },
  ];

  const operationalTiles = [
    {
      id: 'my-gear',
      title: 'My Gear',
      description: 'Deploy, scan, track and return accountable field gear.',
      logo: FIRELINE_MEDIC_BADGE,
      variant: 'primary',
      onClick: () => window.location.assign('/my-gear'),
    },
    {
      id: 'ics-214',
      title: 'ICS 214',
      description: 'Build daily activity logs offline, reuse your crew, and email the completed 214.',
      icon: 'document',
      variant: 'primary',
      onClick: () => window.location.assign('/ics214'),
    },
    ...lineMedicOnlyTiles,
    {
      id: 'radio-reference',
      title: 'Radio Reference',
      description: 'Quick field guides and radio reference material.',
      icon: 'radio',
      variant: 'reference',
      onClick: () => setActiveView('radios'),
    },
    {
      id: 'sac-protocols',
      title: 'Sac County Protocols',
      description: 'Search official County EMS protocols with offline access.',
      icon: 'document',
      variant: 'reference',
      onClick: () => setActiveView('protocols'),
    },
    {
      id: 'saved-iaps',
      title: 'Saved IAPs',
      description: 'Scan, choose and save incident IAP PDFs for offline field reference.',
      icon: 'document',
      variant: 'reference',
      onClick: () => {
        window.history.pushState({}, '', '/saved-iaps');
        window.dispatchEvent(new CustomEvent('smfd:native-route', { detail: { path: '/saved-iaps' } }));
      },
    },
    {
      id: 'drug-calculator',
      title: 'Drug Calculator',
      description: 'Adult + pediatric weight-based doses, draw volumes, and field preparation — works offline.',
      icon: 'calculator',
      variant: 'reference',
      onClick: () => setActiveView('drug-calculator'),
    },
    {
      id: 'fire-weather',
      title: 'Fire Weather',
      description: 'NWS fire-weather forecast for the phone location with offline cache.',
      icon: 'fire',
      variant: 'reference',
      onClick: () => setActiveView('fire-weather'),
    },
  ];

  const availabilityTiles = [
    {
      id: 'medics',
      title: 'Medic Availability',
      description: 'See the current Line Medic roster, status and latest deployment.',
      icon: 'medic',
      onClick: () => setActiveView('medics'),
    },
    {
      id: 'emts',
      title: 'EMT Availability',
      description: 'See the current Line EMT roster, status and latest deployment.',
      icon: 'people',
      onClick: () => setActiveView('emts'),
    },
    {
      id: 'my-availability',
      title: 'My Availability',
      description: 'View your calendar and add or remove your own availability.',
      icon: 'calendar',
      onClick: () => setActiveView('my-availability'),
    },
  ];

  const adminTiles = profile?.is_admin ? [
    {
      id: 'admin',
      title: 'Program Admin',
      description: 'Administrative tools, deployment oversight and system controls.',
      logo: FIRELINE_MEDIC_BADGE,
      variant: 'admin',
      onClick: () => window.location.assign('/admin'),
    },
  ] : [];

  const resourceTiles = [
    {
      id: 'active-fires',
      title: 'Active Fires',
      description: 'Open Watch Duty incident intelligence.',
      icon: 'fire',
      variant: 'external',
      href: 'https://app.watchduty.org',
    },
    {
      id: 'mars',
      title: 'OES MARS',
      description: 'Open the Cal OES MARS portal.',
      icon: 'command',
      variant: 'external',
      href: 'https://engage.caloes.ca.gov/s/login/',
    },
    {
      id: 'whatsapp',
      title: 'WhatsApp Group',
      description: 'Open the Line Medic program group.',
      icon: 'message',
      variant: 'external',
      href: 'https://chat.whatsapp.com/FI58yUt2IHY0sOihLxuOOV?mode=ac_t',
    },
    {
      id: 'elite',
      title: 'Elite Field',
      description: 'Open ImageTrend Elite Field.',
      icon: 'document',
      variant: 'external',
      href: 'https://metrofire.imagetrendelite.com/elite/organizationmetrofire/RunForm/Login',
    },
    {
      id: 'operative-iq',
      title: 'Operative IQ',
      description: 'Open equipment and logistics access.',
      icon: 'link',
      variant: 'external',
      href: 'https://login.operativeiq.com/Login.aspx?identifier=metrofire&ReturnUrl=&LoginType=&check=IE',
    },
  ];

  return (
    <>
      <AddToHomePrompt />
      <div className="app-container">
        <DashboardHeader profile={profile} onSignOut={signOutMember} />

        <div className="dashboard-intro">
          <div className="dashboard-intro-copy">
            <span className="dashboard-eyebrow">OPERATIONS PORTAL</span>
            <h2>Field-ready resources in one place.</h2>
            <p>Deployment tools, availability, field references, external systems and program contacts — organized for quick access from the station or the incident.</p>
          </div>
          <div className="dashboard-access-badge">Member access active</div>
        </div>

        <ActionCenter profile={profile} />

        {adminTiles.length > 0 && (
          <section className="dashboard-section">
            <div className="section-heading">
              <div>
                <span className="section-kicker">PROGRAM ADMINISTRATION</span>
                <h2>Program controls</h2>
                <p>Administrative tools for member access, deployments, inventory and system oversight.</p>
              </div>
            </div>
            <TileGrid tiles={adminTiles} />
          </section>
        )}

        <section className="dashboard-section">
          <div className="section-heading">
            <div>
              <span className="section-kicker">FIELD OPERATIONS</span>
              <h2>Operational tools</h2>
              <p>The things you are most likely to need before, during and after an assignment.</p>
            </div>
          </div>
          <TileGrid tiles={operationalTiles} />
        </section>

        {availabilityTiles.length > 0 && (
          <section className="dashboard-section">
            <div className="section-heading">
              <div>
                <span className="section-kicker">LINE MEDIC PROGRAM</span>
                <h2>Availability</h2>
                <p>View the current Line Medic/EMT rosters or manage your own availability calendar.</p>
              </div>
            </div>
            <TileGrid tiles={availabilityTiles} />
          </section>
        )}

        <section className="dashboard-section">
          <div className="section-heading">
            <div>
              <span className="section-kicker">PROGRAM RESOURCES</span>
              <h2>External systems</h2>
              <p>Fast links to incident, communications, reporting and logistics tools.</p>
            </div>
            <span className="section-note">External links open separately</span>
          </div>
          <TileGrid tiles={resourceTiles} className="tile-grid--resources" />
        </section>

        <ContactView sheetId="1WE_XlmvV8eYEGy0HMmMjhsN8F2xvfe6ht9xfWi0vxxY" sheetName="Contacts" />
      </div>
    </>
  );
}

function SavedIapsPage({ profile }) {
  return (
    <DashboardSubpage
      profile={profile}
      onSignOut={signOutMember}
      onBack={() => window.location.assign('/')}
      kicker="FIELD REFERENCE"
      title="Saved IAPs"
      subtitle="Scan the physical IAP QR, choose the actual PDF, then keep a searchable offline copy on this device."
    >
      <SavedIaps />
    </DashboardSubpage>
  );
}

function Ics214Page({ profile }) {
  return (
    <DashboardSubpage
      profile={profile}
      onSignOut={signOutMember}
      onBack={() => window.location.assign('/')}
      kicker="FIELD DOCUMENTATION"
      title="ICS 214 Activity Log"
      subtitle="Build, retain, print, and email daily ICS 214 activity logs - even when field service is unavailable."
    >
      <Ics214 profile={profile} />
    </DashboardSubpage>
  );
}

function ProgramFeedPage({ profile, postId = '' }) {
  return (
    <DashboardSubpage profile={profile} onSignOut={signOutMember} onBack={() => window.location.assign('/')} kicker="PROGRAM COMMUNICATIONS" title="Program Feed" subtitle="Program updates, acknowledgments, and discussion for Line Medic members.">
      <ProgramFeed profile={profile} initialPostId={postId} />
    </DashboardSubpage>
  );
}

function PrescribedBurnsPage({ profile, burnId = '' }) {
  return (
    <DashboardSubpage profile={profile} onSignOut={signOutMember} onBack={() => window.location.assign('/')} kicker="PROGRAM OPERATIONS" title="Prescribed Burns" subtitle="Upcoming burn opportunities, signup, and roster status.">
      <PrescribedBurns profile={profile} initialBurnId={burnId} />
    </DashboardSubpage>
  );
}

export default function App() {
  const [routeKey, setRouteKey] = useState(
    () => `${window.location.pathname}${window.location.search}${window.location.hash}`
  );

  useEffect(() => {
    const currentRoute = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const syncRoute = event => {
      const requested = String(event?.detail?.path || '').trim();
      setRouteKey(requested.startsWith('/') ? requested : currentRoute());
    };

    window.addEventListener('popstate', syncRoute);
    window.addEventListener('smfd:native-route', syncRoute);

    // Catch a cold-launch Universal Link that arrived before this effect mounted.
    syncRoute();

    return () => {
      window.removeEventListener('popstate', syncRoute);
      window.removeEventListener('smfd:native-route', syncRoute);
    };
  }, []);

  // Native notification routing carries the destination in routeKey as well as
  // updating browser history. Rendering from routeKey avoids depending on
  // Capacitor's WebView history timing during an iOS notification resume.
  const routePath = String(routeKey || '/').split(/[?#]/, 1)[0] || '/';
  const path = routePath.replace(/\/+$/, '') || '/';

  if (path === '/control') return <><ControlCenter /><ControllerVersionBadge /></>;

  if (path === '/privacy') return <PrivacyPolicy />;
  if (path === '/support') return <AppSupport />;
  if (path === '/app-review-demo') return <AppReviewDemo />;
  if (/^\/rems-access(?:\/|$)/i.test(path)) return <RemsQuickAccess />;

  const publicAssetMatch = path.match(/^\/(?:gear|asset)\/([^/]+)$/i);
  if (publicAssetMatch) return <AssetTagPublic assetId={publicAssetMatch[1]} />;

  const handoffMatch = path.match(/^\/handoff\/([^/]+)$/i);
  if (handoffMatch) return <GearHandoffRoute token={handoffMatch[1]} />;

  // Preview-generated native test QRs use the already-shipped /tracking
  // Universal Link path so an Xcode build can receive the token before the
  // production AASA advertises the new /handoff/* path.
  if (path === '/tracking') {
    const handoffToken = String(new URLSearchParams(String(routeKey).split('?')[1]?.split('#')[0] || '').get('handoff') || '').trim();
    if (handoffToken) return <GearHandoffRoute token={handoffToken} />;
  }

  if (path === '/availability') return <LineMedicPublic />;
  if (path === '/staffing') return <DeploymentPublic />;

  if (path === '/tracking' || path === '/my-gear') return <TrackingProtected key={routeKey} />;
  if (path === '/ics214') return <MemberAuthGate>{profile => <Ics214Page profile={profile} />}</MemberAuthGate>;
  if (path === '/saved-iaps') return <MemberAuthGate>{profile => <SavedIapsPage profile={profile} />}</MemberAuthGate>;
  if (path === '/settings') return <MemberAuthGate>{profile => <AppSettings profile={profile} onSignOut={signOutMember} />}</MemberAuthGate>;

  const feedMatch = path.match(/^\/program-feed(?:\/([^/]+))?$/i);
  if (feedMatch) return <MemberAuthGate>{profile => <ProgramFeedPage profile={profile} postId={feedMatch[1] || ''} />}</MemberAuthGate>;
  const burnMatch = path.match(/^\/prescribed-burns(?:\/([^/]+))?$/i);
  if (burnMatch) return <MemberAuthGate>{profile => <PrescribedBurnsPage profile={profile} burnId={burnMatch[1] || ''} />}</MemberAuthGate>;

  if (path === '/admin') return <MemberAuthGate>{profile => <AdminHub profile={profile} />}</MemberAuthGate>;
  if (path === '/admin/attention') return <MemberAuthGate>{profile => <AdminAttention profile={profile} />}</MemberAuthGate>;
  if (path === '/admin/members') return <MemberAuthGate>{profile => <MemberManagement profile={profile} />}</MemberAuthGate>;
  if (path === '/admin/deployments') return <MemberAuthGate>{profile => <DeploymentDashboard profile={profile} />}</MemberAuthGate>;
  if (path === '/admin/recall') return <MemberAuthGate>{profile => <InventoryRecallLookup profile={profile} />}</MemberAuthGate>;
  if (path === '/admin/health') return <MemberAuthGate>{profile => <SystemHealth profile={profile} />}</MemberAuthGate>;
  if (path === '/admin/tags') return <MemberAuthGate>{profile => <AssetTagSetup profile={profile} />}</MemberAuthGate>;
  if (path === '/admin/program-feed') return <MemberAuthGate>{profile => <ProgramFeedAdmin profile={profile} />}</MemberAuthGate>;
  if (path === '/admin/prescribed-burns') return <MemberAuthGate>{profile => <PrescribedBurnsAdmin profile={profile} />}</MemberAuthGate>;
  if (path === '/admin/notifications') return <MemberAuthGate>{profile => <NotificationAudit profile={profile} />}</MemberAuthGate>;
  if (path === '/admin/door-access') return <MemberAuthGate>{profile => <DoorAccessAdmin profile={profile} />}</MemberAuthGate>;

  if (path === '/inventory-admin') return <MemberAuthGate>{profile => <InventoryAdminProtected member={profile} />}</MemberAuthGate>;

  if (path === '/restock-approval') return <MemberAuthGate>{profile => <RestockApprovalPage profile={profile} />}</MemberAuthGate>;

  if (path === '/account/update-password') return <MemberAuthGate>{profile => <MemberDashboard profile={profile} />}</MemberAuthGate>;

  return <MemberAuthGate>{profile => isIndividualRemsProfile(profile) ? <RemsDashboard profile={profile} /> : <MemberDashboard profile={profile} />}</MemberAuthGate>;
}